import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import SignatureAdopt from "../onboarding/SignatureAdopt.jsx";
import PacketFormView from "../onboarding/PacketFormView.jsx";
import { fmtTime, BRAND, GOLD } from "../onboarding/helpers.js";

const ESIGN_TEXT = [
  "Feel At Home Care LLC dba Jybek HomeCare Services (the “Company”) would like to provide your new-hire documents electronically and have you sign them electronically.",
  "By agreeing, you consent to receive these records electronically and to use electronic signatures and initials, which have the same legal effect as handwritten ones under the federal ESIGN Act and New Hampshire’s Uniform Electronic Transactions Act (RSA 294-E).",
  "You may ask for paper copies at any time, at no charge, and you may withdraw this consent for future documents by notifying Human Resources. You need a device with a current web browser or the JyBek app, and the ability to open PDF files, to view and keep your records.",
];

function Shell({ children }) {
  return <div className="max-w-3xl mx-auto">{children}</div>;
}

function Hero({ me }) {
  const pct = me.total ? Math.round((me.done / me.total) * 100) : 0;
  return (
    <section className="rounded-2xl p-6 text-white shadow-lg" style={{ background: "linear-gradient(120deg,#0f3d73,#154E93 58%,#2870b8)" }}>
      <div className="text-blue-100 text-xs tracking-[0.18em] font-semibold">NEW HIRE PACKET · 2026–2027</div>
      <h1 className="text-2xl sm:text-3xl font-bold mt-1">Welcome, {me.employee.name.split(" ")[0]}</h1>
      <p className="text-blue-100 mt-1 text-sm">{me.employee.position || "Caregiver"} · {me.employee.employee_id}</p>
      <div className="mt-5">
        <div className="flex justify-between text-sm mb-1"><span>{me.done} of {me.total} forms complete</span><span>{pct}%</span></div>
        <div className="h-2.5 rounded-full bg-white/20 overflow-hidden"><div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} /></div>
      </div>
    </section>
  );
}

function Overview({ me, reload }) {
  const byStage = useMemo(() => {
    const m = {};
    for (const f of me.forms) (m[f.stage] = m[f.stage] || []).push(f);
    return m;
  }, [me]);
  const next = me.forms.find((f) => f.status !== "signed");
  const p = me.packet;

  return (
    <div className="space-y-5">
      <Hero me={me} />

      {p.status === "certified" && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-green-800 text-sm">
          <b>Your personnel file is complete.</b> Human Resources certified it on {fmtTime(p.certified_at)}. Thank you!
        </div>
      )}
      {me.annual && me.annual.status !== "signed" && (
        <Link to={`/portal/onboarding/${me.annual.id}`} className="block rounded-xl border-2 p-4 bg-amber-50" style={{ borderColor: "#C9A84C" }}>
          <div className="text-xs font-bold tracking-widest" style={{ color: GOLD }}>ACTION NEEDED</div>
          <div className="font-bold text-lg" style={{ color: "#1B3A6B" }}>{me.annual.title}</div>
          <div className="text-sm text-gray-600">Required every year by NH He-P 822.17(t). Takes about 2 minutes.</div>
        </Link>
      )}
      {next && p.status !== "certified" && (
        <Link to={`/portal/onboarding/${next.id}`} className="flex items-center justify-between gap-3 rounded-xl p-4 text-white font-semibold shadow" style={{ backgroundColor: BRAND }}>
          <span>{me.done === 0 ? "Start" : "Continue"}: {next.num} — {next.short || next.title}</span><span aria-hidden>→</span>
        </Link>
      )}
      {!next && p.stage_open < 2 && (
        <div className="rounded-xl border bg-white p-4 text-sm text-gray-700">
          <b>Part one is done.</b> Human Resources will open your orientation and training forms after your orientation session.
        </div>
      )}
      {!next && p.stage_open >= 3 && p.status !== "certified" && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <b>All forms are complete.</b> Human Resources will review and certify your file. Questions? Call {me.hr_phone}.
        </div>
      )}

      {[1, 2, 3].filter((s) => byStage[s]).map((s) => (
        <section key={s} className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-sm" style={{ color: "#1B3A6B" }}>Step {s} · {me.stage_names[s]}</div>
          <ul className="divide-y">
            {byStage[s].map((f) => (
              <li key={f.id}>
                <Link to={`/portal/onboarding/${f.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                  <span className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${f.status === "signed" ? "bg-green-600 text-white" : "border-2 border-gray-300 text-gray-400"}`}>{f.status === "signed" ? "✓" : ""}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[11px] font-bold tracking-wider" style={{ color: GOLD }}>{f.num}</span>
                    <span className="block text-sm font-medium text-gray-800 truncate">{f.title}</span>
                  </span>
                  <span className="text-xs text-gray-500 hidden sm:block">{f.status === "signed" ? fmtTime(f.signed_at) : ""}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {p.stage_open < 2 && (
        <section className="bg-white border rounded-xl p-4 text-sm text-gray-500">
          <b className="text-gray-700">Step 2 · Orientation &amp; training</b> and <b className="text-gray-700">Step 3 · Final sign-off</b> open after your orientation with Human Resources.
        </section>
      )}

      <section className="grid sm:grid-cols-2 gap-3">
        <a href={api.onboarding.handbookUrl} target="_blank" rel="noreferrer" className="bg-white border rounded-xl p-4 hover:bg-slate-50">
          <div className="font-semibold" style={{ color: BRAND }}>Employee Handbook</div>
          <div className="text-sm text-gray-500">2026–2027 edition (PDF). You acknowledge receiving it on Form 18.</div>
        </a>
        <div className="bg-white border rounded-xl p-4">
          <div className="font-semibold" style={{ color: BRAND }}>My signature</div>
          <div className="flex items-center gap-3 mt-1">
            {me.images?.sig && <img src={me.images.sig} alt="signature" className="h-9" />}
            {me.images?.init && <img src={me.images.init} alt="initials" className="h-7" />}
          </div>
          <div className="text-xs text-gray-500 mt-1">Adopted as {p.adopted_name} ({p.adopted_initials})</div>
        </div>
      </section>

      {me.documents?.length > 0 && (
        <section className="bg-white border rounded-xl p-4">
          <div className="font-semibold mb-2" style={{ color: BRAND }}>My signed copies</div>
          <ul className="text-sm space-y-1">
            {me.documents.map((d) => (
              <li key={d.id} className="flex justify-between gap-2"><a className="underline" href={api.onboarding.documentUrl(d.id)}>{d.label}</a><span className="text-xs text-gray-500">{fmtTime(d.created_at)}</span></li>
            ))}
          </ul>
        </section>
      )}
      <p className="text-xs text-gray-500 text-center pb-4">Questions about your paperwork? Call Human Resources at {me.hr_phone}. <button onClick={reload} className="underline ml-1">Refresh</button></p>
    </div>
  );
}

export default function PortalOnboarding() {
  const { formId } = useParams();
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.onboarding.me().then(setMe).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [formId]);

  if (error) return <Shell><div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-5">{error}</div></Shell>;
  if (!me) return <div className="text-gray-500">Loading your new hire packet…</div>;
  if (!me.packet) return (
    <Shell>
      <div className="bg-white border rounded-xl p-6 text-center">
        <h1 className="text-xl font-bold" style={{ color: BRAND }}>No new hire packet yet</h1>
        <p className="text-sm text-gray-600 mt-2">Human Resources has not assigned onboarding paperwork to your account. If you were expecting it, call {me.hr_phone || "888-717-5009 Ext. 101"}.</p>
      </div>
    </Shell>
  );

  const p = me.packet;

  if (p.status === "paper_requested") return (
    <Shell>
      <div className="bg-white border rounded-xl p-6 space-y-3">
        <h1 className="text-xl font-bold" style={{ color: BRAND }}>Paper copies requested</h1>
        <p className="text-sm text-gray-700">Human Resources will give you paper forms to sign. Call {me.hr_phone} with any questions.</p>
        <button className="text-sm underline" style={{ color: BRAND }} onClick={async () => { await api.onboarding.consent({ agree: true }); load(); }}>Changed your mind? Sign electronically instead</button>
      </div>
    </Shell>
  );

  if (!p.consent_at) return (
    <Shell>
      <div className="bg-white border rounded-2xl shadow-sm p-6 space-y-4">
        <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>STEP 1 OF 2 · BEFORE YOU BEGIN</div>
        <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Consent to electronic records and signatures</h1>
        {ESIGN_TEXT.map((t) => <p key={t} className="text-sm text-gray-700">{t}</p>)}
        <label className="flex items-start gap-2 text-sm font-medium">
          <input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>I agree to use electronic records and electronic signatures for my employment documents.</span>
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <button disabled={!agree || busy} onClick={async () => { setBusy(true); try { await api.onboarding.consent({ agree: true }); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } }}
            className="px-6 py-3 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: BRAND }}>I agree — continue</button>
          <button disabled={busy} onClick={async () => { if (!window.confirm("Request paper forms instead?")) return; await api.onboarding.consent({ paper: true }); load(); }}
            className="px-6 py-3 rounded-lg border font-semibold text-gray-700">I prefer paper forms</button>
        </div>
      </div>
    </Shell>
  );

  if (!p.adopted) return (
    <Shell>
      <div className="bg-white border rounded-2xl shadow-sm p-6 space-y-4">
        <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>STEP 2 OF 2 · ADOPT YOUR SIGNATURE</div>
        <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Your signature and initials</h1>
        <p className="text-sm text-gray-600">You will initial each point and sign each form with what you adopt here.</p>
        <SignatureAdopt defaultName={me.employee.name} onAdopt={async (b) => { await api.onboarding.adopt(b); await load(); }} />
      </div>
    </Shell>
  );

  if (formId) {
    const seq = me.forms.map((f) => f.id);
    const idx = seq.indexOf(formId);
    const next = idx >= 0 ? seq[idx + 1] : null;
    const io = {
      key: formId,
      load: () => api.onboarding.form(formId),
      save: (v) => api.onboarding.save(formId, v),
      stamp: (k) => api.onboarding.stamp(formId, k),
      submit: () => api.onboarding.submit(formId),
      upload: (d) => api.onboarding.upload(formId, d),
      fileUrl: (id) => api.onboarding.fileUrl(id),
    };
    return <PacketFormView mode="employee" io={io} backTo="/portal/onboarding" nextTo={next ? `/portal/onboarding/${next}` : "/portal/onboarding"} />;
  }

  return <Shell><Overview me={me} reload={load} /></Shell>;
}

/** Compact card for the portal home screen. Renders nothing when there is no open packet. */
export function OnboardingBanner() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.onboarding.me().then(setMe).catch(() => setMe(null)); }, []);
  if (!me?.packet) return null;
  const p = me.packet;
  const annualOpen = me.annual && me.annual.status !== "signed";
  if (p.status === "certified" && !annualOpen) return null;
  const allDone = me.done >= me.total;
  if (allDone && !annualOpen) return null;
  return (
    <Link to="/portal/onboarding" className="block rounded-2xl border-2 bg-white p-5 shadow-sm" style={{ borderColor: "#C9A84C" }}>
      <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>{annualOpen ? "ANNUAL ATTESTATION DUE" : "NEW HIRE PACKET"}</div>
      <div className="flex items-center justify-between gap-3 mt-1">
        <div>
          <div className="text-lg font-bold" style={{ color: "#1B3A6B" }}>{annualOpen ? me.annual.title : `${me.done} of ${me.total} forms complete`}</div>
          <div className="text-sm text-gray-600">{annualOpen ? "Sign your yearly He-P 822.17(t) attestation." : "Review, initial and sign your onboarding paperwork."}</div>
        </div>
        <span className="px-4 py-2 rounded-lg text-white text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: BRAND }}>Open →</span>
      </div>
    </Link>
  );
}
